/**
 * ProgressBar.js - Unified component for displaying content progress
 *
 * Handles both movie and series progress display with consistent styling.
 * Replaces the duplicate functionality in ThumbnailHoverModal and SeriesProgress.
 */

import React from 'react';
import './ProgressBar.css';

/**
 * ProgressBar Component
 *
 * @param {Object} props
 * @param {Object} props.progressData - Progress data object
 * @param {number} props.progressData.progress - Progress percentage (0-1)
 * @param {number} props.progressData.point - Current time in seconds
 * @param {number} props.progressData.duration - Total duration in seconds
 * @param {string} props.progressData.title - Content title
 * @param {boolean} props.progressData.isSeries - Whether this is series content
 * @param {number} props.progressData.episodeNumber - Episode number (series only)
 * @param {number} props.progressData.seasonNumber - Season number (series only)
 * @param {string} props.variant - Display variant: 'modal', 'card', 'inline' (default: 'modal')
 * @param {string} props.size - Size variant: 'small', 'medium', 'large' (default: 'medium')
 * @param {boolean} props.showProgressBar - Whether to show the progress bar (default: true)
 * @param {boolean} props.showEpisodeInfo - Whether to show episode info (series only, default: true)
 * @param {string} props.className - Additional CSS classes
 */
const ProgressBar = ({
  progressData,
  variant = 'modal',
  size = 'medium',
  showProgressBar = true,
  showEpisodeInfo = true,
  className = '',
}) => {
  // For series, show episode info even if there's no progress (for "next episode" items)
  if (!progressData) {
    return null;
  }

  

  // For movies, only show if there's progress
  if (!progressData.isSeries && !progressData.progress) {
    return null;
  }

  // Format time helper - Netflix style (Xm or Xh Ym)
  const formatTime = seconds => {
    if (!seconds || isNaN(seconds)) return '0m';
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);

    if (hours > 0) {
      return `${hours}h${minutes > 0 ? `${minutes}m` : ''}`;
    } else {
      return `${minutes}m`;
    }
  };

  // Size-based styling
  const sizeClasses = {
    small: {
      container: 'progress-small',
      title: 'progress-title-small',
      progress: 'progress-bar-small',
      text: 'progress-text-small',
    },
    medium: {
      container: 'progress-medium',
      title: 'progress-title-medium',
      progress: 'progress-bar-medium',
      text: 'progress-text-medium',
    },
    large: {
      container: 'progress-large',
      title: 'progress-title-large',
      progress: 'progress-bar-large',
      text: 'progress-text-large',
    },
  };

  const currentSize = sizeClasses[size] || sizeClasses.medium;

  return (
    <div className={`progress-bar ${variant} ${currentSize.container} ${className}`}>
      {/* Episode Information (for series) */}
      {progressData.isSeries && showEpisodeInfo && (
        <div className="progress-episode-info">
          <span className={`progress-episode ${currentSize.text}`}>
            S{progressData.seasonNumber} E{progressData.episodeNumber} • {progressData.title}
          </span>
          <span className={`progress-time ${currentSize.text}`}>
            {formatTime(progressData.point)} of {formatTime(progressData.duration)}
          </span>
        </div>
      )}

      {/* Time Progress (for movies) */}
      {!progressData.isSeries && (
        <div className="progress-time-info">
          {showProgressBar && (
            <div className={`progress-container ${currentSize.progress}`}>
              <div className="progress-fill" style={{ width: `${progressData.progress * 100}%` }} />
            </div>
          )}
          <span className={`progress-time ${currentSize.text}`}>
            {formatTime(progressData.point)} of {formatTime(progressData.duration)}
          </span>
        </div>
      )}

      {/* Progress Bar (for series - keep stacked) */}
      {progressData.isSeries && showProgressBar && (
        <div className={`progress-container ${currentSize.progress}`}>
          <div className="progress-fill" style={{ width: `${progressData.progress * 100}%` }} />
        </div>
      )}
    </div>
  );
};

export default ProgressBar;
